/**
 * Batch Sync API
 *
 * Handles sending offline mutations to the server.
 * - Batchable mutations (domain operations) are grouped and sent to the batch endpoint
 * - Non-batchable mutations are sent individually to their respective endpoints
 *
 * The backend batch endpoint expects:
 * mutation { batch(input: { operations: [{ name: "createFiber", args: {...} }] }) }
 */

import { requestGQL } from '../graphQlClient';
import { isBlobUrl } from '../utils';

import { MutationOperation } from './types';
import { getRemoteUrlMapping } from './uploadedPhotoRegistry';

/**
 * List of mutation names that support batching
 * These correspond to mutations marked with @batchable in the backend schema
 */
export const BATCHABLE_MUTATIONS: Set<string> = new Set([
  // Fiber domain mutations
  'createFiber',
  'patchFiber',
  'deleteFiber',
  'createTwistedPair',
  'patchTwistedPair',
  'deleteTwistedPair',
  'deleteOutletPhoto',
  'uploadOutletPhoto',
  'createOutlet',
  'deleteOutlet',
  'updateOutletLocation',
  'createBundle',
  'deleteBundle',
  'patchBundle',
  'createTrunk',
  'deleteTrunk',
  'patchTrunk',
  'createODF',
  'deleteODF',
  'patchODF',
  'createTechnicalODF',
  'deleteTechnicalODF',
  'patchTechnicalODF',
  'createSwitch',
  'deleteSwitch',
  'patchSwitch',
  'createTechnicalSwitch',
  'deleteTechnicalSwitch',
  'patchTechnicalSwitch',
  'createCrossConnectionPanel',
  'deleteCrossConnectionPanel',
  'patchCrossConnectionPanel',
  'deleteTpOutletPhoto',
  'uploadTpOutletPhoto',
  'createTpOutlet',
  'deleteTpOutlet',
  'patchTpOutlet',
  'createWifiDot',
  'deleteWifiDot',
  'patchWifiDot',
  'createTechnicalEquipment',
  'deleteTechnicalEquipment',
  'patchTechnicalEquipment',
  'patchPorts',
  'createFireBreach',
  'deleteFireBreach',
  'patchFireBreach',
  'createRack',
  'deleteRack',
  'patchRack',
  'createCableRoute',
  'deleteCableRoute',
  'patchCableRoute',
  // Radio domain mutations
  'createCoaxial',
  'patchCoaxial',
  'deleteCoaxial',
  'createBaseStation',
  'deleteBaseStation',
  'patchBaseStation',
  'createRepeater',
  'deleteRepeater',
  'patchRepeater',
  'createCoupler',
  'deleteCoupler',
  'patchCoupler',
  'createRadioUnit',
  'deleteRadioUnit',
  'patchRadioUnit',
  'createDropAntenna',
  'deleteDropAntenna',
  'patchDropAntenna',
  'createInputAntenna',
  'deleteInputAntenna',
  'patchInputAntenna',
  'createSplitter',
  'deleteSplitter',
  'patchSplitter',
  'createCombiner',
  'deleteCombiner',
  'patchCombiner',
]);

/**
 * Operation priority for ordering within a batch
 * Lower numbers are processed first
 */
const OPERATION_PRIORITY: Record<string, number> = {
  // Create operations should happen first
  createOutlet: 1,
  createTpOutlet: 1,
  createFiber: 1,
  createTwistedPair: 1,
  createBundle: 1,
  createTrunk: 1,
  createODF: 1,
  createTechnicalODF: 1,
  createSwitch: 1,
  createTechnicalSwitch: 1,
  createCrossConnectionPanel: 1,
  createWifiDot: 1,
  createTechnicalEquipment: 1,
  createFireBreach: 1,
  createBaseStation: 1,
  createRepeater: 1,
  createCoupler: 1,
  createRadioUnit: 1,
  createDropAntenna: 1,
  createInputAntenna: 1,
  createSplitter: 1,
  createCombiner: 1,
  // Update/patch operations happen after creates
  patchFiber: 2,
  patchTwistedPair: 2,
  patchBundle: 2,
  patchTrunk: 2,
  patchODF: 2,
  patchTechnicalODF: 2,
  patchSwitch: 2,
  patchTechnicalSwitch: 2,
  patchCrossConnectionPanel: 2,
  patchTpOutlet: 2,
  patchWifiDot: 2,
  patchTechnicalEquipment: 2,
  patchPorts: 2,
  updateOutletLocation: 2,
  patchBaseStation: 2,
  patchRepeater: 2,
  patchCoupler: 2,
  patchRadioUnit: 2,
  patchDropAntenna: 2,
  patchInputAntenna: 2,
  patchSplitter: 2,
  patchCombiner: 2,
  // Photo uploads should happen after creates (they reference the created entity)
  uploadOutletPhoto: 3,
  uploadTpOutletPhoto: 3,
  // Delete operations happen last
  deleteOutletPhoto: 4,
  deleteTpOutletPhoto: 4,
  deleteFiber: 5,
  deleteTwistedPair: 5,
  deleteOutlet: 5,
  deleteBundle: 5,
  deleteTrunk: 5,
  deleteODF: 5,
  deleteTechnicalODF: 5,
  deleteSwitch: 5,
  deleteTechnicalSwitch: 5,
  deleteCrossConnectionPanel: 5,
  deleteTpOutlet: 5,
  deleteWifiDot: 5,
  deleteTechnicalEquipment: 5,
  deleteFireBreach: 5,
  deleteBaseStation: 5,
  deleteRepeater: 5,
  deleteCoupler: 5,
  deleteRadioUnit: 5,
  deleteDropAntenna: 5,
  deleteInputAntenna: 5,
  deleteSplitter: 5,
  deleteCombiner: 5,
};

/**
 * Get priority for a mutation operation (lower = processed first)
 */
function getOperationPriority(mutationName: string): number {
  return OPERATION_PRIORITY[mutationName] ?? 3; // Default to middle priority
}

/**
 * Sort mutations by priority to ensure correct ordering
 * (e.g., createOutlet before uploadOutletPhoto)
 */
function sortMutationsByPriority(mutations: MutationOperation[]): MutationOperation[] {
  return [...mutations].sort((a, b) => {
    const priorityA = getOperationPriority(a.mutationName);
    const priorityB = getOperationPriority(b.mutationName);
    return priorityA - priorityB;
  });
}

/**
 * Check if a mutation can be batched
 */
export function isBatchableMutation(mutationName: string): boolean {
  return BATCHABLE_MUTATIONS.has(mutationName);
}

/**
 * Batch operation input format expected by the backend
 */
export type BatchedOperationInput = {
  name: string;
  args: Record<string, unknown>;
};

/**
 * Result status for a batched operation
 */
export type BatchedOperationStatus = 'OK' | 'ERROR' | 'SKIPPED';

/**
 * Result for a single batched operation
 */
export type BatchedOperationResult = {
  name: string;
  propertyId: number;
  status: BatchedOperationStatus;
  output: unknown;
};

/**
 * Response from the batch mutation
 */
export type BatchSyncResponse = {
  success: boolean;
  results: Array<{
    mutationId: string;
    success: boolean;
    error?: string;
    data?: unknown;
  }>;
};

/**
 * Result from sending a single (non-batchable) mutation
 */
export type SingleMutationResult = {
  mutationId: string;
  success: boolean;
  error?: string;
  data?: unknown;
};

/**
 * GraphQL mutation for batch operations
 */
const BATCH_MUTATION = `
  mutation Batch($input: BatchInput!) {
    batch(input: $input) {
      name
      propertyId
      status
      output
    }
  }
`;

/**
 * Find the details object in an area mutation input.
 * Area mutations have a structure like: { input: { propertyId, odfDetails: { id, photosURLs, ... } } }
 * The details key varies by entity type (odfDetails, switchDetails, trunkDetails, etc.)
 */
function findDetailsObject(input: Record<string, unknown>): {
  detailsKey: string | null;
  details: Record<string, unknown> | null;
} {
  const detailsKeys = [
    'odfDetails',
    'switchDetails',
    'trunkDetails',
    'bundleDetails',
    'technicalEquipmentDetails',
    'crossConnectionPanelDetails',
    'wifiDotDetails',
    'fireBreachDetails',
    'rackDetails',
    'cableRouteDetails',
    'basicObjectDetails', // Used by rack, fireBreach, cableRoute
    'tpOutletDetails',
    // Radio domain
    'coaxialDetails',
    'baseStationDetails',
    'repeaterDetails',
    'couplerDetails',
    'radioUnitDetails',
    'dropAntennaDetails',
    'inputAntennaDetails',
    'splitterDetails',
    'combinerDetails',
  ];

  const foundKey = detailsKeys.find((key) => input[key] && typeof input[key] === 'object');

  if (foundKey) {
    return { detailsKey: foundKey, details: input[foundKey] as Record<string, unknown> };
  }

  return { detailsKey: null, details: null };
}

/**
 * Inject uploaded photo URLs into CREATE and UPDATE mutation payloads.
 * This is used for area objects where photos are added during creation (offline)
 * and the uploaded URLs need to be included in the CREATE/UPDATE mutation.
 *
 * Area mutations have a nested structure like:
 * { input: { propertyId, odfDetails: { id, photosURLs, ... } } }
 */
function injectUploadedPhotoUrls(mutation: MutationOperation): MutationOperation {
  // Only process CREATE/UPDATE mutations
  if (!['CREATE', 'UPDATE'].includes(mutation.type)) {
    return mutation;
  }

  const payload = mutation.payload as Record<string, unknown>;
  const input = (payload.input ?? payload) as Record<string, unknown>;

  // Find the details object (e.g., odfDetails, switchDetails)
  const { detailsKey, details } = findDetailsObject(input);

  // Get the entity ID - it could be at input.id (simple) or input.*Details.id (area)
  let targetObject = input;
  let targetKey: string | null = null;

  if (details && detailsKey) {
    // Area mutation with nested details
    targetObject = details;
    targetKey = detailsKey;
  }

  // Merge uploaded URLs with any existing photosURLs in the target object
  const existingPhotos = (targetObject.photosURLs as string[] | undefined) ?? [];
  const mergedPhotos = existingPhotos.map((url) => (isBlobUrl(url) ? getRemoteUrlMapping(url) : url)).filter(Boolean); // filter to make sure we don't include any nulls by mistake (they'd break the app)

  // Create updated payload with merged photos
  let updatedPayload: Record<string, unknown>;

  if (targetKey) {
    // Nested details structure (area mutations)
    const updatedDetails = { ...targetObject, photosURLs: mergedPhotos };
    const updatedInput = { ...input, [targetKey]: updatedDetails };
    updatedPayload = 'input' in payload ? { ...payload, input: updatedInput } : updatedInput;
  } else {
    // Simple structure
    const updatedInput = { ...input, photosURLs: mergedPhotos };
    updatedPayload = 'input' in payload ? { ...payload, input: updatedInput } : updatedInput;
  }

  return {
    ...mutation,
    payload: updatedPayload,
  };
}

/**
 * Send batchable mutations to the batch endpoint
 * @param mutations - Array of mutations that are batchable
 * @returns Results for each mutation
 */
export async function sendBatchableMutations(mutations: MutationOperation[]): Promise<BatchSyncResponse> {
  if (mutations.length === 0) {
    return { success: true, results: [] };
  }

  // Sort mutations by priority to ensure correct ordering
  // (e.g., createOutlet must happen before uploadOutletPhoto)
  const sortedMutations = sortMutationsByPriority(mutations);

  // Inject uploaded photo URLs into CREATE and UPDATE mutations before sending
  // This ensures that photos uploaded during offline area creation are included
  const mutationsWithPhotos = sortedMutations.map(injectUploadedPhotoUrls);

  // Create a mapping from sorted mutation to original index for result mapping
  const sortedToOriginalIndex = new Map<string, number>();
  mutations.forEach((mutation, index) => {
    sortedToOriginalIndex.set(mutation.id, index);
  });

  // Convert mutations to batch operations format
  // The batch resolver expects args in the format { input: { propertyId, ... } }
  // because it uses idFromInput(args, ['input', 'propertyId']) to extract the property ID
  const operations: BatchedOperationInput[] = mutationsWithPhotos.map((mutation) => {
    const payload = mutation.payload as Record<string, unknown>;

    // Special handling for connection mutations (createFiber, createTwistedPair, etc.)
    // These expect { input: { propertyId, fiber: {...} } } or { input: { propertyId, twistedPair: {...} } }
    // But the hook stores just the fiber/twistedPair data in the payload
    const connectionMutationKeys: Record<string, string> = {
      createFiber: 'fiber',
      patchFiber: 'fiber',
      createTwistedPair: 'twistedPair',
      patchTwistedPair: 'twistedPair',
      createCoaxial: 'coaxial',
      patchCoaxial: 'coaxial',
    };

    const entityKey = connectionMutationKeys[mutation.mutationName];
    if (entityKey && mutation.propertyId) {
      // Wrap the payload with propertyId and the correct entity key
      return {
        name: mutation.mutationName,
        args: {
          input: {
            propertyId: mutation.propertyId,
            [entityKey]: payload,
          },
        },
      };
    }

    // If payload is already wrapped in 'input', use it as-is
    // Otherwise, wrap it in 'input' to match the GraphQL resolver's expected format
    const args = 'input' in payload ? payload : { input: payload };

    return {
      name: mutation.mutationName,
      args,
    };
  });

  // eslint-disable-next-line no-console
  console.log('[BatchSync] Sending batch with', operations.length, 'operations (sorted by priority):', operations);

  try {
    const response = (await requestGQL(BATCH_MUTATION, { input: { operations } })) as {
      batch?: BatchedOperationResult[];
    } | null;

    const batchResults = response?.batch;

    if (!batchResults) {
      throw new Error('Batch mutation returned no results');
    }

    // Map results back to mutation IDs (using sortedMutations order)
    const results = sortedMutations.map((mutation, index) => {
      const result = batchResults[index];

      if (!result) {
        return {
          mutationId: mutation.id,
          success: false,
          error: 'No result returned for this operation',
        };
      }

      const isSuccess = result.status === 'OK';

      return {
        mutationId: mutation.id,
        success: isSuccess,
        error: isSuccess ? undefined : String(result.output || result.status),
        data: isSuccess ? result.output : undefined,
      };
    });

    // eslint-disable-next-line no-console
    console.log('[BatchSync] Batch response:', results);

    return {
      success: results.every((r) => r.success),
      results,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[BatchSync] Batch request failed:', error);
    throw error;
  }
}

/**
 * Send a single non-batchable mutation to its original endpoint
 * @param mutation - The mutation to send
 * @returns Result of the mutation
 */
export async function sendSingleMutation(mutation: MutationOperation): Promise<SingleMutationResult> {
  // eslint-disable-next-line no-console
  console.log('[SingleSync] Sending mutation:', mutation.mutationName);

  // If we have a stored GraphQL document, use the original endpoint
  if (mutation.graphqlDocument) {
    // eslint-disable-next-line no-console
    console.log('[SingleSync] Using stored GraphQL document for non-batchable mutation');

    try {
      const response = await requestGQL(mutation.graphqlDocument, mutation.payload as Record<string, unknown>);

      // eslint-disable-next-line no-console
      console.log('[SingleSync] Response:', response);

      return {
        mutationId: mutation.id,
        success: true,
        data: response,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // eslint-disable-next-line no-console
      console.error('[SingleSync] Mutation failed:', errorMessage);

      return {
        mutationId: mutation.id,
        success: false,
        error: errorMessage,
      };
    }
  }

  // Fallback: No GraphQL document stored - try batch endpoint with single operation
  // eslint-disable-next-line no-console
  console.warn(
    '[SingleSync] No GraphQL document stored for non-batchable mutation. Attempting batch endpoint as fallback.',
  );

  try {
    // Send as a single-item batch instead of using the old individual endpoint
    const response = (await requestGQL(BATCH_MUTATION, {
      input: {
        operations: [
          {
            name: mutation.mutationName,
            args: mutation.payload as Record<string, unknown>,
          },
        ],
      },
    })) as { batch?: BatchedOperationResult[] } | null;

    const result = response?.batch?.[0];
    const isSuccess = result?.status === 'OK';

    // eslint-disable-next-line no-console
    console.log('[SingleSync] Response:', result);

    return {
      mutationId: mutation.id,
      success: isSuccess,
      error: isSuccess ? undefined : String(result?.output || result?.status || 'Unknown error'),
      data: isSuccess ? result?.output : undefined,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // eslint-disable-next-line no-console
    console.error('[SingleSync] Mutation failed:', errorMessage);

    return {
      mutationId: mutation.id,
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Categorize mutations into batchable and non-batchable groups
 */
export function categorizeMutations(mutations: MutationOperation[]): {
  batchable: MutationOperation[];
  nonBatchable: MutationOperation[];
} {
  const batchable: MutationOperation[] = [];
  const nonBatchable: MutationOperation[] = [];

  mutations.forEach((mutation) => {
    if (isBatchableMutation(mutation.mutationName)) {
      batchable.push(mutation);
    } else {
      nonBatchable.push(mutation);
    }
  });

  return { batchable, nonBatchable };
}

/**
 * Legacy function for backward compatibility
 * Now delegates to the new categorized sync
 */
export async function sendBatchSync(mutations: MutationOperation[]): Promise<BatchSyncResponse> {
  const { batchable, nonBatchable } = categorizeMutations(mutations);
  const allResults: BatchSyncResponse['results'] = [];

  // Send non-batchable mutations first (individually) - must be sequential
  // eslint-disable-next-line no-restricted-syntax
  for (const mutation of nonBatchable) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await sendSingleMutation(mutation);
      allResults.push(result);
    } catch (error) {
      // If a non-batchable mutation fails, we still continue with others
      // but we track the failure for proper error handling
      allResults.push({
        mutationId: mutation.id,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  // Send batchable mutations as a batch
  if (batchable.length > 0) {
    const batchResponse = await sendBatchableMutations(batchable);
    allResults.push(...batchResponse.results);
  }

  return {
    success: allResults.every((r) => r.success),
    results: allResults,
  };
}
